import { HttpInterceptorFn, provideHttpClient, withInterceptors } from '@angular/common/http';
import {
    DOCUMENT,
    EnvironmentProviders,
    InjectionToken,
    Provider,
    Signal,
    inject,
    makeEnvironmentProviders,
    provideAppInitializer,
    provideEnvironmentInitializer,
    signal
} from '@angular/core';
import {
    AbstractSecurityStorage,
    AuthFeature,
    EventTypes,
    OidcSecurityService,
    OpenIdConfiguration,
    PublicEventsService,
    StsConfigLoader,
    provideAuth,
    withAppInitializerAuthCheck
} from 'angular-auth-oidc-client';
import { Observable, filter, first, map, of } from 'rxjs';

import {
    ACCESS_TOKEN_PROVIDER,
    AUTH_INFO_PROVIDER,
    BpsImpersonationService,
    CipError,
    ImpersonableUser,
    ImpersonationService,
    UserInfoService,
    accessTokenInterceptor,
    impersonationInterceptor
} from '@mobi/rwc-utils-azure-ng-jslib';
import { GuardRegistryService, Logger } from '@mobi/rwc-utils-common-ng-jslib';
import { TelemetryUserContext } from '@mobi/rwc-utils-observability-ng-jslib';

import { OAUTH_CONFIG } from './authentication-config.provider';
import { AuthenticationService } from './authentication.service';
import { CipAccessTokenService } from './internal/cip-access-token.service';
import { CipAuthHelperService } from './internal/cip-auth-helper.service';
import { CIP_CONFIG_ID, CUSTOM_CIP_CONFIG, CipAuthConfig } from './internal/cip-auth.config';
import { CIP_ERROR_HANDLER, CipErrorHandler, cipErrorInterceptor } from './internal/cip-error.interceptor';
import { CipSecurityStorageService } from './internal/cip-security-storage.service';
import { CipUserInfoService } from './internal/cip-user-info.service';
import { DefaultCipErrorHandler } from './internal/default-cip-error-handler.service';
import { CipImpersonationService } from './internal/impersonation/cip-impersonation.service';
import { CUSTOM_IMPERSONABLE_USERS } from './internal/impersonation/impersonable-users.service';

export const cipInterceptors: () => HttpInterceptorFn[] = () => [accessTokenInterceptor];

/**
 * Provides oauth authentication for the application.
 * By default, it uses CIP / KeyCloak as IDP but can be overridden using `withConfig()`.
 */
export function provideAppAuthentication(...features: CipAuthFeature<CipAuthFeatureKind>[]): EnvironmentProviders {
    const interceptors = features.flatMap(feature => feature.interceptorFns ?? []);
    interceptors.push(accessTokenInterceptor);
    const providers: (Provider | EnvironmentProviders)[] = [
        { provide: OAUTH_CONFIG, multi: true, useClass: CipAuthConfig },
        { provide: ACCESS_TOKEN_PROVIDER, useClass: CipAccessTokenService },
        { provide: AUTH_INFO_PROVIDER, useValue: 'CIP' as const },
        provideHttpClient(withInterceptors(interceptors)),
        provideEnvironmentInitializer(() => inject(GuardRegistryService).checkAzureAuthGuard())
    ];
    features.forEach(feature => providers.push(...feature.providers));

    if (!features.some(feature => feature.kind === CipAuthFeatureKind.AutoLogin)) {
        providers.push(provideAuthOidcClient(withAppInitializerAuthCheck()));
    }
    if (!features.some(feature => feature.kind === CipAuthFeatureKind.SessionStorage)) {
        providers.push({ provide: AbstractSecurityStorage, useClass: CipSecurityStorageService });
    }

    return makeEnvironmentProviders(providers);
}

class OAuthConfigLoader extends StsConfigLoader {
    #configs = inject(OAUTH_CONFIG).map(config => config.loadConfig());
    #logger = inject(Logger).createCustomLogger('OAUTH');

    override loadConfigs(): Observable<OpenIdConfiguration[]> {
        this.#logger.debug(`Load [${this.#configs.map(c => c.configId).join()}] OAuth configs`);
        return of(this.#configs);
    }
}

/**
 * Only for internal use. You should use `provideAppAuthentication()` instead.
 */
export function provideAuthOidcClient(...features: AuthFeature[]) {
    return makeEnvironmentProviders([
        provideAuth(
            {
                loader: {
                    provide: StsConfigLoader,
                    useClass: OAuthConfigLoader
                }
            },
            // cannot use withAppInitializerAuthCheck() because of the autoLogin the cip feature.
            ...features
        ),
        provideEnvironmentInitializer(() => inject(GuardRegistryService).checkAuthOidcClientGuard())
    ]);
}

/**
 * Automatically log in the user, no need for guards.
 *
 * Note: Impossible to have a public/anonymous route.
 */
export function withAutoLogin(): CipAuthFeature<CipAuthFeatureKind.AutoLogin> {
    return makeCipAuthFeature(CipAuthFeatureKind.AutoLogin, [
        provideAppInitializer(() => {
            const oauthService = inject(OidcSecurityService);
            const authHelperService = inject(CipAuthHelperService);
            // When the tab wakes up, the OIDC library tries to silently renew the access token.
            // After 30 minutes, the refresh token is invalid and the IdP returns 400.
            // In that case, trigger a full login.
            inject(PublicEventsService)
                .registerForEvents()
                .pipe(filter(evt => evt.type === EventTypes.SilentRenewFailed))
                .subscribe(() => authHelperService.authorize(CIP_CONFIG_ID));
            return oauthService.checkAuthMultiple().pipe(
                map(res => !!res.find(r => r.configId === CIP_CONFIG_ID)?.isAuthenticated),
                authHelperService.processAuthentication(CIP_CONFIG_ID)
            );
        }),
        provideAuthOidcClient()
    ]);
}

/**
 * to provide a custom OpenID configuration, e.g. to load it from a server or to use different configurations for different environments.
 */
export function withConfig(configFactory: () => OpenIdConfiguration): CipAuthFeature<CipAuthFeatureKind.Config> {
    return makeCipAuthFeature(CipAuthFeatureKind.Config, [{ provide: CUSTOM_CIP_CONFIG, useFactory: configFactory }]);
}

interface ImpersonationOptions {
    /** usesBps is to uses this library without KeyCloak (CIP). */
    usesBps?: boolean;
    /** Optional factory to provide custom impersonable users. In preprod they are merged with test users. */
    impersonableUsers?: () => Observable<ImpersonableUser[]>;
}

/**
 * Adds user impersonation support.
 */
export function withImpersonation(
    options: ImpersonationOptions = {}
): CipAuthFeature<CipAuthFeatureKind.Impersonation> {
    let providers: (Provider | EnvironmentProviders)[];
    if (options.usesBps) {
        providers = [{ provide: ImpersonationService, useClass: BpsImpersonationService }];
    } else {
        providers = [{ provide: ImpersonationService, useClass: CipImpersonationService }];
    }
    if (options.impersonableUsers) {
        providers.push({ provide: CUSTOM_IMPERSONABLE_USERS, useFactory: options.impersonableUsers });
    }
    return makeCipAuthFeature(
        CipAuthFeatureKind.Impersonation,
        [
            ...providers,
            // The UserInfoService is only required until all cloud b2e SPA migrated to CIP
            { provide: UserInfoService, useClass: CipUserInfoService },
            // Ensure the service subscribes to router events as early as possible, to avoid missing NavigationStart event.
            provideEnvironmentInitializer(() => inject(ImpersonationService).onBehalfOf)
        ],
        options.usesBps ? [impersonationInterceptor] : []
    );
}

/**
 * Sets the authenticated user in OpenTelemetry for traces and logs.
 */
export function withAuthenticatedUserTelemetry(): CipAuthFeature<CipAuthFeatureKind.AuthenticatedUserTelemetry> {
    return makeCipAuthFeature(CipAuthFeatureKind.AuthenticatedUserTelemetry, [
        provideEnvironmentInitializer(() => {
            const userContext = inject(TelemetryUserContext);

            inject(AuthenticationService)
                .getUserData()
                .pipe(first())
                .subscribe(userData => {
                    userContext.setAuthenticatedUser(userData.preferred_username);
                    userContext.setImpersonator(userData.impersonator?.username);
                });
        })
    ]);
}
/**
 * Sets the email (or given value) of the logged-in user as login hint in local storage,
 * so that it can be used by CIP / KeyCloak to prefill the username field on the login page.
 */
export function withLoginHint(
    hint: 'email' | 'preferred_username' = 'email'
): CipAuthFeature<CipAuthFeatureKind.LoginHint> {
    return makeCipAuthFeature(CipAuthFeatureKind.LoginHint, [
        provideEnvironmentInitializer(() => {
            const localStorage = inject(DOCUMENT).defaultView?.localStorage;

            inject(AuthenticationService)
                .getUserData()
                .pipe(first())
                .subscribe(userData => localStorage?.setItem('cip-login-hint', userData[hint]));
        })
    ]);
}

/**
 * when withCipErrorHandler() is used without providing a custom error handler,
 * the CipError signal will be provided and updated with the latest CipError.
 */
export const CIP_ERROR = new InjectionToken<Signal<CipError | undefined>>('CIP_ERROR', {
    factory: () => signal(undefined)
});

/**
 * Provides a custom error handler for CIP authentication errors.
 * The handler will be called when a CipError occurs during authentication.
 *
 * @param errorHandlerFactory Factory function that returns the error handler.
 * Can be used with inject() to get services, e.g.:
 * `withCipErrorHandler(() => inject(MyService).handleError)`
 */
export function withCipErrorHandler(
    errorHandlerFactory?: () => CipErrorHandler
): CipAuthFeature<CipAuthFeatureKind.ErrorHandler> {
    const cipErrorHandlerFactory = errorHandlerFactory ?? (() => inject(DefaultCipErrorHandler).handleError);
    return makeCipAuthFeature(
        CipAuthFeatureKind.ErrorHandler,
        [
            ...(errorHandlerFactory
                ? []
                : [{ provide: CIP_ERROR, useFactory: () => inject(DefaultCipErrorHandler).error }]),
            { provide: CIP_ERROR_HANDLER, useFactory: cipErrorHandlerFactory }
        ],
        [cipErrorInterceptor]
    );
}

/**
 * This uses the SessionStorage instead of more secured CipSecurityStorageService (in memory) to save the credentials.
 * This should only be used temporary for SPA using WebComponents until they are replaced by jslib, as this is a security risk.
 * @see https://engineering-handbook.app.mobiliar.ch/rwc-stack/latest/concepts/local_storage.html#_what_not_to_store_in_browser_storage
 */
export function withUnsafeCredentialsStorage(): CipAuthFeature<CipAuthFeatureKind.SessionStorage> {
    return makeCipAuthFeature(CipAuthFeatureKind.SessionStorage, []);
}

interface CipAuthFeature<KindT extends CipAuthFeatureKind> {
    kind: KindT;
    providers: (Provider | EnvironmentProviders)[];
    interceptorFns?: HttpInterceptorFn[];
}

const enum CipAuthFeatureKind {
    Config = 0,
    AutoLogin = 1,
    Impersonation = 2,
    AuthenticatedUserTelemetry = 3,
    LoginHint = 4,
    ErrorHandler = 5,
    SessionStorage = 6
}

function makeCipAuthFeature<KindT extends CipAuthFeatureKind>(
    kind: KindT,
    providers: (Provider | EnvironmentProviders)[],
    interceptorFns?: HttpInterceptorFn[]
): CipAuthFeature<KindT> {
    return { kind, providers, interceptorFns };
}
